/**
 * Quick-add modal.
 *
 * Fetches the rendered HTML of the `product-quick-add` section via Shopify's
 * `?section_id=` endpoint, injects it into the modal, and re-executes the
 * inline <script> tags that come with the response (since assigning innerHTML
 * does NOT run them). The section template is self-contained — its own
 * <style>/<script> tags arrive together with the markup, so the modal renders
 * correctly on every page without requiring extra CSS to be loaded globally.
 */
class QuickAddModal {
  constructor() {
    this.modal = document.getElementById("quick-add-modal");
    this.modalContent = document.getElementById("quick-add-modal-content");
    this.closeButton = document.querySelector(".quick-add-modal__close");
    this.currentProductHandle = null;

    this.initEvents();
  }

  initEvents() {
    if (this.closeButton) this.closeButton.addEventListener("click", () => this.close());
    if (this.modal) {
      this.modal.addEventListener("click", (e) => {
        if (e.target === this.modal) this.close();
      });
    }
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen()) this.close();
    });

    // Delegated handler for any quick-add button on the page.
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".quick-add-btn");
      if (!btn) return;
      e.preventDefault();

      const productHandle = btn.dataset.productHandle;
      const hasVariants = btn.dataset.hasVariants === "true";
      const variantId = btn.dataset.variantId;
      if (!productHandle) return;

      // Single-variant products skip the modal and add directly.
      if (!hasVariants && variantId) {
        this.addToCartDirect(variantId, btn);
      } else {
        this.open(productHandle);
      }
    });
  }

  isOpen() {
    return this.modal && this.modal.style.display === "flex";
  }

  open(productHandle) {
    if (!this.modal || !this.modalContent) return;
    if (this.currentProductHandle === productHandle && this.isOpen()) return;

    this.currentProductHandle = productHandle;
    this.showLoading();
    this.modal.style.display = "flex";
    document.body.style.overflow = "hidden";
    this.fetchProduct(productHandle);
  }

  close() {
    if (!this.modal) return;
    this.modal.style.display = "none";
    document.body.style.overflow = "";
    this.currentProductHandle = null;
    // Clear the injected content so old data can't leak into the next open.
    if (this.modalContent) this.modalContent.innerHTML = "";
  }

  showLoading() {
    if (!this.modalContent) return;
    this.modalContent.innerHTML =
      '<div class="quick-add-loading">' +
      '<p class="loading-text">Loading product</p>' +
      '<div class="loading-dots"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>' +
      "</div>";
  }

  showError() {
    if (!this.modalContent) return;
    this.modalContent.innerHTML =
      '<div class="quick-add-error"><p>Failed to load product. Please try again.</p></div>';
  }

  /**
   * Fetch the product-quick-add section for a given handle, then inject the
   * raw response into the modal. The response includes the section's own
   * <style> and <script> tags; we re-execute the scripts so the variant
   * picker / qty / add-to-cart wiring runs against the freshly-injected DOM.
   */
  async fetchProduct(productHandle) {
    try {
      const url = `/products/${encodeURIComponent(productHandle)}?section_id=product-quick-add`;
      const response = await fetch(url, { headers: { Accept: "text/html" } });
      if (!response.ok) throw new Error("Failed to fetch product");

      const text = await response.text();
      this.injectSection(text);
    } catch (err) {
      console.error("Quick add error:", err);
      this.showError();
    }
  }

  /**
   * Parse the section response, pull out the `.qa-prod` markup + its
   * sibling <script>/<style> tags, inject them into the modal, then
   * re-execute the scripts so they actually run.
   *
   * Shopify wraps the section render in <div id="shopify-section-...">. We
   * use the whole section wrapper as our injection root so styles/scripts
   * inside it come along automatically.
   */
  injectSection(html) {
    if (!this.modalContent) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Section render wrapper, fall back to the qa-prod root, fall back to body.
    let source =
      doc.getElementById("shopify-section-product-quick-add") ||
      doc.querySelector("[data-qa-prod]") ||
      doc.body;
    if (!source) throw new Error("Quick-add section markup not found");

    // Wipe any previous content (also strips orphaned styles from prior open).
    this.modalContent.innerHTML = "";

    // Move children one by one, re-creating <script> nodes so they execute.
    Array.from(source.childNodes).forEach((node) => {
      this.modalContent.appendChild(this.cloneExecutable(node));
    });
    // If `source` IS the wrapper itself (shopify-section-... div), also copy
    // its children up to a flat structure. Already handled above.

    // Defensive: if no qa-prod root ended up inside, surface an error.
    if (!this.modalContent.querySelector("[data-qa-prod]")) {
      this.showError();
    }
  }

  /**
   * Clone a node, replacing any <script> elements (in itself or descendants)
   * with fresh <script> elements built via document.createElement so the
   * browser actually runs them. Without this step, scripts injected via
   * innerHTML / appendChild of cloned nodes never execute.
   */
  cloneExecutable(node) {
    if (node.nodeType === 1 /* ELEMENT_NODE */ && node.tagName === "SCRIPT") {
      return this.makeRunnableScript(node);
    }
    const clone = node.cloneNode(false);
    if (node.childNodes && node.childNodes.length) {
      Array.from(node.childNodes).forEach((child) => {
        clone.appendChild(this.cloneExecutable(child));
      });
    }
    // After children are reattached, replace any nested script tags too.
    if (clone.querySelectorAll) {
      Array.from(clone.querySelectorAll("script")).forEach((s) => {
        s.parentNode.replaceChild(this.makeRunnableScript(s), s);
      });
    }
    return clone;
  }

  makeRunnableScript(original) {
    const fresh = document.createElement("script");
    Array.from(original.attributes || []).forEach((attr) => {
      fresh.setAttribute(attr.name, attr.value);
    });
    if (original.textContent) fresh.textContent = original.textContent;
    return fresh;
  }

  /**
   * Single-variant fast path: add the variant directly without opening the
   * modal. Publishes a cart-update event with `action: "add"` so the
   * cart drawer (cart-drawer-kaleido.js) refreshes + opens automatically.
   */
  async addToCartDirect(variantId, button) {
    const labelEl = button.querySelector(".quick-add-btn-label");
    const original = labelEl ? labelEl.textContent : button.textContent;
    // Arabic state texts to match the rest of the site.
    const isArabic = (document.documentElement.lang || "").toLowerCase().startsWith("ar");

const ADDING = isArabic ? "يتم الاضافه..." : "Adding...";
const ADDED = isArabic ? "اضاف" : "Added";
const ERROR = isArabic ? "نفذت الكمية" : "Out Of Stock";

    button.disabled = true;
    if (labelEl) labelEl.textContent = ADDING;
    else button.textContent = ADDING;

    try {
      const cartUrl = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : "/cart/add") + ".js";
      const res = await fetch(cartUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || "Failed to add to cart");

      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          action: "add",
          source: "quick-add-direct",
          productVariantId: variantId,
          cartData: data,
        });
      }

      if (labelEl) labelEl.textContent = ADDED;
      else button.textContent = ADDED;
      setTimeout(() => {
        button.disabled = false;
        if (labelEl) labelEl.textContent = original;
        else button.textContent = original;
      }, 1200);
    } catch (err) {
      console.error("Error adding to cart:", err);
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "quick-add-direct",
          message: err && err.message,
        });
      }
      if (labelEl) labelEl.textContent = ERROR;
      else button.textContent = ERROR;
      setTimeout(() => {
        button.disabled = false;
        if (labelEl) labelEl.textContent = original;
        else button.textContent = original;
      }, 1800);
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  window.quickAddModal = new QuickAddModal();

  // Card swatch click → swap the card's image. Kept here because product
  // cards exist on every page and benefit from a single delegated handler.
  document.addEventListener("click", function (e) {
    const colorOption = e.target.closest(".card-product__color-option:not(.disabled)");
    if (!colorOption) return;
    e.preventDefault();
    if (colorOption.getAttribute("aria-current") === "true") return;

    const productCard = colorOption.closest(".card-product");
    if (!productCard) return;

    const desktopImage = productCard.querySelector(".card-product__image-link.hidden-mobile .card-product__image");
    const mobileImage = productCard.querySelector(".card-product__image-container.visible-mobile .card-product__image");

    colorOption
      .closest(".card-product__colors")
      .querySelectorAll(".card-product__color-option")
      .forEach((b) => b.setAttribute("aria-current", "false"));
    colorOption.setAttribute("aria-current", "true");

    const newSrc = colorOption.dataset.imageSrc;
    const newMobileSrc = colorOption.dataset.imageSrcMobile;
    const newAlt = colorOption.dataset.imageAlt;

    if (desktopImage && newSrc) desktopImage.src = newSrc;
    if (mobileImage && newMobileSrc) mobileImage.src = newMobileSrc;
    if (desktopImage) desktopImage.alt = newAlt;
    if (mobileImage) mobileImage.alt = newAlt;
  });
});
